import { colyseusClient, type LobbyState, type PlayerSlot, type SlotType, type Room } from '../lobby/colyseus.js';
import { fetchFactions, fetchFactionsInGame, type FactionDef } from '../lobby/api.js';
import { auth } from '../auth/state.js';
import type { Router } from '../router.js';
import { el } from './login.js';
import { mountInGame, unmountInGame, updateInGame } from './in_game.js';
import { startMusic } from '../sound/music.js';
import { getTutorialSession } from './tutorial.js';

function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (err) => { clearTimeout(t); reject(err); },
    );
  });
}

/**
 * Room screen — the in-room UI. The user joins a room (via lobby browser or
 * deep link), and we show:
 *   - 8 slots: open / human / AI (3 difficulties) / closed
 *   - Faction picker (14 kingdoms, color-coded)
 *   - Map preview (re-rendered by host via map.regen)
 *   - Host-only controls: change slot type, change seed, toggle fog, start
 *
 * The room handle is either passed via window.__wolRoom (set by lobby_browser)
 * or attempted via joinById on the URL. If neither succeeds, we kick back to
 * the lobby browser.
 */

let activeRoom: Room | null = null;
let unsub: Array<() => void> = [];
/** Bumped on every unmount / new mount so an in-flight reconnect loop
 *  from a previous room instance knows to stop. */
let reconnectGeneration = 0;

export function unmountRoom(): void {
  reconnectGeneration += 1;
  removeReconnectBanner();
  for (const fn of unsub) fn();
  unsub = [];
  unmountInGame();
  if (activeRoom) {
    void activeRoom.leave().catch(() => null);
    activeRoom = null;
  }
}

export async function renderRoom(
  container: HTMLElement,
  router: Router,
  roomId: string,
): Promise<void> {
  container.innerHTML = '';
  container.classList.add('overlay-screen');

  const wrap = el('div', 'room-wrap');
  const status = el('p', 'room-status', 'Connexion à la salle…');
  wrap.appendChild(status);
  container.appendChild(wrap);

  // Reuse the room handle that was just created/joined, or rejoin by id.
  const stash = (window as unknown as { __wolRoom?: Room }).__wolRoom;
  let room: Room | null = stash ?? null;
  (window as unknown as { __wolRoom?: Room }).__wolRoom = undefined;

  if (!room || room.roomId !== roomId) {
    // Try a passwordless join first — ranked rooms, public salons,
    // tutorial rooms and the "Revenir à ma chasse" rejoin path all
    // accept this. Only prompt the player for a password if the
    // server actually refuses with "Mot de passe incorrect", which
    // happens exclusively on private salons that the joiner has
    // never been part of before.
    const tryJoin = async (password?: string): Promise<Room> => withTimeout(
      colyseusClient.joinById(roomId, password ? { password } : {}),
      8000,
      'Le serveur multijoueur ne répond pas.',
    );
    try {
      room = await tryJoin();
    } catch (firstErr) {
      const msg = (firstErr as Error)?.message ?? '';
      if (msg.toLowerCase().includes('mot de passe')) {
        // The room needs a password — ask for it now (just one round-trip
        // worth of friction, only for private salons).
        const pw = window.prompt('Mot de passe du salon :', '') ?? '';
        if (!pw) {
          router.navigate('/');
          return;
        }
        try {
          room = await tryJoin(pw);
        } catch (secondErr) {
          status.textContent = (secondErr as Error)?.message ?? 'Mot de passe incorrect.';
          const back = document.createElement('button');
          back.type = 'button';
          back.className = 'btn-primary';
          back.textContent = 'Retour aux salons';
          back.style.marginTop = '12px';
          back.addEventListener('click', () => router.navigate('/'));
          wrap.appendChild(back);
          return;
        }
      } else {
        // Anything else (full room, server unreachable, kicked, …) is
        // surfaced as-is — no password retry would help.
        status.textContent = msg || 'Impossible de rejoindre.';
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'btn-primary';
        back.textContent = 'Retour aux salons';
        back.style.marginTop = '12px';
        back.addEventListener('click', () => router.navigate('/'));
        wrap.appendChild(back);
        return;
      }
    }
  }
  activeRoom = room;

  // Use the in-game variant so the barbarian raid event has a faction
  // entry to colour its banner / chips. The lobby's faction picker
  // calls `fetchFactions()` directly (no barbare) — only the in-game
  // render path needs the NPC faction injected.
  const factions = await fetchFactionsInGame().catch(() => []);

  // Now render the actual UI inside `wrap`. We rebuild on every state change.
  status.remove();

  const header = el('div', 'room-header');
  wrap.appendChild(header);
  const body = el('div', 'room-body');
  wrap.appendChild(body);
  const slotsHost = el('div', 'room-slots');
  body.appendChild(slotsHost);
  const sidePanel = el('div', 'room-side');
  body.appendChild(sidePanel);
  const footer = el('div', 'room-footer');
  wrap.appendChild(footer);

  let inGameMounted = false;
  const screen = container; // alias for clarity

  const renderAll = (state: LobbyState): void => {
    if (state.phase === 'lobby') {
      // Tear down any in-game overlay if we somehow came back to lobby.
      if (inGameMounted) {
        unmountInGame();
        inGameMounted = false;
      }
      // During a tutorial, the lobby phase is just a transient hand-off
      // (the hub auto-sends faction.set + game.start the moment the
      // room is created). Showing the lobby UI for that fraction of a
      // second feels like a flash bug — render a neutral "préparation"
      // screen instead so the player only ever sees a clean overlay.
      if (getTutorialSession()) {
        renderTutorialPrepScreen(wrap, header, slotsHost, sidePanel, footer);
        screen.style.display = '';
        wrap.style.display = '';
        return;
      }
      // Make sure the lobby layout is on screen — and restore the
      // four container divs in case they were hidden by a previous
      // tutorial-prep splash on the same Room instance.
      screen.style.display = '';
      wrap.style.display = '';
      header.style.display = '';
      slotsHost.style.display = '';
      sidePanel.style.display = '';
      footer.style.display = '';
      // Drop any leftover tutorial-prep block from a previous render.
      wrap.querySelectorAll('.tutorial-prep').forEach((n) => n.remove());
      renderHeader(header, state, room!, router);
      renderSlots(slotsHost, state, factions, room!);
      renderSide(sidePanel, state, factions, room!);
      renderFooter(footer, state, room!, router);
    } else {
      // In-game: hide the screen overlay, mount the 3D map + game HUD.
      screen.style.display = 'none';
      const ctx = { room: room!, state, factions };
      if (!inGameMounted) {
        void mountInGame(ctx);
        inGameMounted = true;
      } else {
        // Coalesce: Colyseus can deliver several patches within one
        // frame (AI action chains, resource ticks…). updateInGame does a
        // full HUD rebuild (innerHTML) so running it once per frame on
        // the LATEST state — instead of once per patch — cuts DOM churn
        // massively on mobile. `state` is the live schema object, so
        // the deferred call always sees the newest values.
        latestCtx = ctx;
        if (pendingUpdate === 0) {
          pendingUpdate = requestAnimationFrame(() => {
            pendingUpdate = 0;
            if (latestCtx) updateInGame(latestCtx);
          });
        }
      }
    }
  };
  let latestCtx: Parameters<typeof updateInGame>[0] | null = null;
  let pendingUpdate = 0;
  unsub.push(() => {
    if (pendingUpdate !== 0) { cancelAnimationFrame(pendingUpdate); pendingUpdate = 0; }
    latestCtx = null;
  });

  // Render on every state patch. colyseus.js's `onStateChange` is callable to
  // register, and exposes `.remove(handler)` to unregister.
  const stateHandler = (state: unknown): void => {
    renderAll(state as LobbyState);
  };
  room.onStateChange(stateHandler);
  unsub.push(() => activeRoom?.onStateChange.remove(stateHandler));

  // Handle room close / kick / disconnection.
  //
  // Code 1000 = we left on purpose (Quitter / navigation). Anything
  // else is an abnormal drop — typically a phone that switched apps or
  // locked its screen (iOS kills the WebSocket within seconds), or a
  // flaky mobile network. The server keeps the participant's seat open
  // for 120 s via allowReconnection, so instead of dumping the player
  // on the hub we try to re-link the SAME seat with the reconnection
  // token. On success the room is re-rendered in place — the 3D scene
  // is reused (same seed) so it's near-instant.
  const leaveHandler = (code: number): void => {
    const token = room?.reconnectionToken ?? '';
    const st = room?.state as unknown as LobbyState | undefined;
    const phase = st?.phase ?? 'lobby';
    // Spectators hold no seat → the server offers them no reconnection
    // window; a retry loop would only waste 30 s before failing.
    let isParticipant = false;
    const myId = auth.user?.id ?? '';
    st?.participants?.forEach((p) => { if (p && p.id === myId) isParticipant = true; });
    activeRoom = null;
    if (code === 1000 || !token || phase === 'lobby' || phase === 'ended' || !isParticipant) {
      if (code !== 1000) status.textContent = `Salle fermée (code ${code}).`;
      router.navigate('/');
      return;
    }
    void attemptReconnect(token, roomId, container, router);
  };
  room.onLeave(leaveHandler);
  unsub.push(() => activeRoom?.onLeave.remove(leaveHandler));

  // First render — state may already be present.
  if (room.state) renderAll(room.state as unknown as LobbyState);
}

// ----------------------------------------------------------------------------

const RECONNECT_DELAYS_MS = [800, 1500, 2500, 4000, 5000, 5000, 5000, 5000];

/**
 * Try to re-link a dropped connection to the same participant seat.
 * Shows a small fixed banner while retrying (up to ~30 s of attempts,
 * comfortably inside the server's 120 s allowReconnection window —
 * beyond that the socket is usually not coming back and the hub's
 * "Revenir à ma chasse" button covers the long-absence case).
 */
async function attemptReconnect(
  token: string,
  roomId: string,
  container: HTMLElement,
  router: Router,
): Promise<void> {
  const gen = reconnectGeneration;
  const banner = showReconnectBanner();
  for (let i = 0; i < RECONNECT_DELAYS_MS.length; i++) {
    await new Promise((r) => setTimeout(r, RECONNECT_DELAYS_MS[i]));
    if (gen !== reconnectGeneration) return; // user navigated away meanwhile
    banner.textContent = `Connexion perdue — reconnexion… (${i + 1}/${RECONNECT_DELAYS_MS.length})`;
    try {
      const fresh = await withTimeout(
        colyseusClient.reconnect(token),
        6000,
        'timeout',
      );
      if (gen !== reconnectGeneration) {
        void fresh.leave().catch(() => null);
        return;
      }
      // Success: hand the new Room to renderRoom via the same stash the
      // hub uses, tear down the stale handler set (their room is dead)
      // and re-render in place. mountInGame reuses the existing 3D
      // renderer when the seed matches, so only the HUD re-mounts.
      removeReconnectBanner();
      for (const fn of unsub) fn();
      unsub = [];
      (window as unknown as { __wolRoom?: Room }).__wolRoom = fresh;
      await renderRoom(container, router, roomId);
      return;
    } catch {
      // Keep trying — the next delay is longer.
    }
  }
  if (gen !== reconnectGeneration) return;
  removeReconnectBanner();
  window.alert('Impossible de rétablir la connexion. Tu peux revenir dans ta partie depuis le hub.');
  router.navigate('/');
}

function showReconnectBanner(): HTMLElement {
  removeReconnectBanner();
  const b = document.createElement('div');
  b.className = 'room-reconnect-banner';
  b.textContent = 'Connexion perdue — reconnexion…';
  document.body.appendChild(b);
  return b;
}

function removeReconnectBanner(): void {
  document.querySelectorAll('.room-reconnect-banner').forEach((n) => n.remove());
}

/**
 * Replace the lobby UI with a single "Préparation du tuto…" splash
 * while the tutorial room is finishing its auto-setup (faction.set,
 * ready, slot.set ai, game.start). Without this, the player sees a
 * half-rendered lobby for ~300 ms before the dice ceremony kicks in.
 *
 * We don't just clear the inner content of the lobby containers —
 * the containers themselves carry dark rounded-rect chrome that
 * would still show as four empty placeholders. We hide them via
 * `display: none` so the splash is the only visible thing.
 */
function renderTutorialPrepScreen(
  wrap: HTMLElement,
  header: HTMLElement,
  slotsHost: HTMLElement,
  sidePanel: HTMLElement,
  footer: HTMLElement,
): void {
  header.style.display = 'none';
  slotsHost.style.display = 'none';
  sidePanel.style.display = 'none';
  footer.style.display = 'none';
  header.innerHTML = '';
  slotsHost.innerHTML = '';
  sidePanel.innerHTML = '';
  footer.innerHTML = '';
  // Avoid stacking duplicate prep blocks across state ticks.
  if (wrap.querySelector('.tutorial-prep')) return;
  const block = el('div', 'tutorial-prep');
  const icon = document.createElement('img');
  icon.src = '/sprites/iconchat_interrogation.png';
  icon.className = 'tutorial-prep-icon';
  icon.alt = '';
  block.appendChild(icon);
  block.appendChild(el('div', 'tutorial-prep-title', 'Préparation du tuto…'));
  block.appendChild(
    el('div', 'tutorial-prep-sub', 'Les chatons s\'installent autour du jardin, étirent leurs pattes et aiguisent leurs griffes. MIAOU patience !'),
  );
  wrap.appendChild(block);
}

function renderHeader(host: HTMLElement, state: LobbyState, room: Room, router: Router): void {
  host.innerHTML = '';
  // Title block (room name + lock + code) — left side
  const titleBlock = el('div', 'room-header-title-block');
  const title = el('h1', 'room-title', state.name || 'SALLE');
  if (state.isPrivate) title.appendChild(el('span', 'room-lock', ' 🔒'));
  titleBlock.appendChild(title);
  titleBlock.appendChild(el(
    'p',
    'room-sub',
    `Code : ${room.roomId} · Phase : ${state.phase === 'lobby' ? 'configuration' : 'démarrage'}`,
  ));
  host.appendChild(titleBlock);

  // Leave button → iconchat_refus only (no text). Big square button.
  const leave = document.createElement('button');
  leave.type = 'button';
  leave.className = 'room-leave-icon';
  leave.title = 'Quitter la salle';
  const lImg = document.createElement('img');
  lImg.src = '/sprites/iconchat_refus.png';
  lImg.className = 'room-leave-icon-img';
  lImg.alt = 'Quitter';
  leave.appendChild(lImg);
  leave.addEventListener('click', () => {
    void room.leave().catch(() => null);
    router.navigate('/');
  });
  host.appendChild(leave);
}

function renderSlots(host: HTMLElement, state: LobbyState, factions: FactionDef[], room: Room): void {
  host.innerHTML = '';
  host.appendChild(el('h2', 'panel-title', 'Royaumes participants'));
  const me = auth.user?.id ?? '';
  const isHost = state.hostUserId === me;

  // Set of factionIds already claimed (used to disable taken kingdoms in
  // every slot's dropdown).
  const taken = new Set<string>();
  state.slots.forEach((s) => {
    if (s.factionId) taken.add(s.factionId);
  });

  const list = el('div', 'slots-list');
  state.slots.forEach((slot: PlayerSlot) => {
    const row = el('div', 'slot-row');
    if (slot.type === 'human' && slot.id === me) row.classList.add('slot-me');
    if (slot.type === 'closed' || slot.type === 'open') row.classList.add('slot-empty');

    // Left: index chip + name
    const left = el('div', 'slot-row-left');
    left.appendChild(el('span', 'slot-index', `#${slot.index + 1}`));
    const nameWrap = el('div', 'slot-name-wrap');
    const nameLine = el('div', 'slot-name');
    nameLine.textContent = slotDisplayName(slot);
    if (slot.isHost) {
      const hostBadge = el('span', 'slot-host-badge');
      const hostImg = document.createElement('img');
      hostImg.src = '/sprites/crown.png';
      hostImg.className = 'inline-icon';
      hostImg.alt = 'Hôte';
      hostBadge.appendChild(hostImg);
      nameLine.appendChild(hostBadge);
    }
    // Ready badge next to the name — iconchat_valide if ready, iconchat_refus
    // if not. Shown for every active slot (human or AI). AI slots are
    // server-flagged ready=true so they always show the green check.
    if (slot.type === 'human' || slot.type.startsWith('ai_')) {
      const readyBadge = el('span',
        slot.ready ? 'slot-ready-badge slot-ready-badge-ok' : 'slot-ready-badge slot-ready-badge-no');
      const icon = document.createElement('img');
      icon.src = slot.ready ? '/sprites/iconchat_valide.png' : '/sprites/iconchat_refus.png';
      icon.className = 'inline-icon';
      icon.alt = slot.ready ? 'Prêt' : 'Pas prêt';
      readyBadge.appendChild(icon);
      readyBadge.appendChild(document.createTextNode(slot.ready ? 'Prêt' : 'Pas prêt'));
      nameLine.appendChild(readyBadge);
    }
    nameWrap.appendChild(nameLine);
    left.appendChild(nameWrap);
    row.appendChild(left);

    // Middle: faction dropdown — visible when the slot is filled (human or AI).
    // For HUMANS: only the local player picks for themselves.
    // For AI: only the host picks.
    const filled = slot.type === 'human' || slot.type.startsWith('ai_');
    const canEditFaction =
      (slot.type === 'human' && slot.id === me) ||
      (isHost && slot.type.startsWith('ai_'));
    if (filled) {
      row.appendChild(buildFactionDropdown(slot, factions, taken, canEditFaction, (fid) => {
        if (slot.type === 'human' && slot.id === me) {
          room.send('faction.set', { factionId: fid });
        } else if (slot.type.startsWith('ai_')) {
          room.send('slot.faction.set', { index: slot.index, factionId: fid });
        }
      }));
    } else {
      row.appendChild(el('div', 'slot-faction-placeholder', ''));
    }

    // Right: host-only type buttons (open / ai_easy / ai_medium / ai_hard / closed).
    // Hidden for human slots (you don't morph a human into an AI).
    if (isHost && slot.type !== 'human') {
      const ctrls = el('div', 'slot-ctrls');
      for (const t of ['open', 'ai_easy', 'ai_medium', 'ai_hard', 'closed'] as SlotType[]) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'slot-ctrl' + (slot.type === t ? ' active' : '');
        btn.textContent = slotShortLabel(t);
        btn.title = slotLongLabel(t);
        btn.addEventListener('click', () => room.send('slot.set', { index: slot.index, type: t }));
        ctrls.appendChild(btn);
      }
      // "Aléatoire" — rolls a random AI difficulty + a random unused
      // faction in one click. Sits right after "IA·D". It's never the
      // "active" highlighted state (it's a one-shot action, not a
      // persistent slot type), so it has its own distinct styling.
      const randBtn = document.createElement('button');
      randBtn.type = 'button';
      randBtn.className = 'slot-ctrl slot-ctrl-random';
      randBtn.textContent = 'Aléatoire';
      randBtn.title = 'IA surprise — difficulté + royaume au hasard (jamais un doublon)';
      randBtn.addEventListener('click', () => room.send('slot.random', { index: slot.index }));
      ctrls.appendChild(randBtn);
      row.appendChild(ctrls);
    } else if (slot.type === 'human') {
      // Keep the right column width consistent — show an empty placeholder.
      row.appendChild(el('div', 'slot-ctrls slot-ctrls-empty', ''));
    }

    list.appendChild(row);
  });
  host.appendChild(list);
}

/**
 * Build a custom dropdown that picks a faction. Renders as a button (icon
 * + name) which expands to a panel with every kingdom. Native `<select>`
 * can't embed images, so we roll our own.
 */
function buildFactionDropdown(
  slot: PlayerSlot,
  factions: FactionDef[],
  taken: Set<string>,
  canEdit: boolean,
  onPick: (factionId: string) => void,
): HTMLElement {
  const wrap = el('div', 'slot-faction-dd');
  if (!canEdit) wrap.classList.add('slot-faction-dd-locked');

  // Trigger button shows the current pick (or "choisir un royaume").
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'slot-faction-dd-trigger';
  trigger.disabled = !canEdit;
  const renderTrigger = (): void => {
    trigger.innerHTML = '';
    if (slot.factionId) {
      const f = factions.find((x) => x.id === slot.factionId);
      if (f) {
        const flag = document.createElement('img');
        flag.src = `/sprites/${f.id}_flag.png`;
        flag.className = 'slot-faction-flag';
        flag.alt = f.name;
        trigger.appendChild(flag);
        const lbl = el('span', 'slot-faction-name');
        lbl.textContent = f.name.toUpperCase();
        lbl.style.color = f.color;
        trigger.appendChild(lbl);
      } else {
        trigger.textContent = '— royaume —';
      }
    } else {
      trigger.textContent = '— choisir un royaume —';
    }
    if (canEdit) {
      const chev = el('span', 'slot-faction-chev');
      chev.textContent = '▼';
      trigger.appendChild(chev);
    }
  };
  renderTrigger();
  wrap.appendChild(trigger);

  if (!canEdit) return wrap;

  // Floating panel — list of factions. Toggled by trigger click. Click
  // outside to close.
  const panel = el('div', 'slot-faction-panel');
  for (const f of factions) {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'slot-faction-cell';
    const isCurrent = f.id === slot.factionId;
    const isTaken = !isCurrent && taken.has(f.id);
    if (isCurrent) cell.classList.add('selected');
    if (isTaken) {
      cell.classList.add('taken');
      cell.disabled = true;
    }
    const flag = document.createElement('img');
    flag.src = `/sprites/${f.id}_flag.png`;
    flag.className = 'slot-faction-cell-flag';
    flag.alt = f.name;
    cell.appendChild(flag);
    const textWrap = el('span', 'slot-faction-cell-text');
    const lbl = el('span', 'slot-faction-cell-name');
    lbl.textContent = f.name.toUpperCase();
    lbl.style.color = f.color;
    textWrap.appendChild(lbl);
    if (f.lore) {
      const loreEl = el('span', 'slot-faction-cell-lore', f.lore);
      textWrap.appendChild(loreEl);
    }
    cell.appendChild(textWrap);
    // Lore on the cell's native title too — readable on hover even if
    // the inline lore text gets truncated in narrow viewports.
    if (f.lore) cell.title = f.lore;
    cell.addEventListener('click', (e) => {
      e.stopPropagation();
      panel.classList.remove('open');
      if (!isTaken && !isCurrent) onPick(f.id);
    });
    panel.appendChild(cell);
  }
  wrap.appendChild(panel);

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    // Close any other open dropdown first.
    document.querySelectorAll('.slot-faction-panel.open').forEach((p) => {
      if (p !== panel) p.classList.remove('open');
    });
    const willOpen = !panel.classList.contains('open');
    panel.classList.toggle('open');
    if (willOpen) positionFactionDropdown(panel, trigger);
  });

  // Click outside → close.
  if (!factionDropdownOutsideListener) {
    factionDropdownOutsideListener = (): void => {
      document.querySelectorAll('.slot-faction-panel.open').forEach((p) => {
        p.classList.remove('open');
      });
    };
    document.addEventListener('click', factionDropdownOutsideListener);
  }

  return wrap;
}

let factionDropdownOutsideListener: (() => void) | null = null;

/**
 * Position the faction-picker dropdown so it always stays on screen.
 * Default placement is BELOW the trigger; if there isn't enough room there
 * we flip it up. The panel's max-height is also constrained to whatever
 * vertical space is available, so even with many factions it never
 * overflows the viewport (the panel scrolls internally instead).
 */
function positionFactionDropdown(panel: HTMLElement, trigger: HTMLElement): void {
  // Reset overrides from any previous open.
  panel.classList.remove('slot-faction-panel-up');
  panel.style.top = '';
  panel.style.bottom = '';
  panel.style.maxHeight = '';

  // Measure rendered panel + trigger relative to the viewport.
  const triggerRect = trigger.getBoundingClientRect();
  const panelHeight = panel.offsetHeight;
  const viewportH = window.innerHeight;
  const margin = 12;
  const roomBelow = viewportH - triggerRect.bottom - margin;
  const roomAbove = triggerRect.top - margin;

  if (panelHeight <= roomBelow) {
    // Fits below — default placement, nothing to do.
    return;
  }
  if (roomAbove > roomBelow) {
    // More room above: flip UP. Cap to fit.
    panel.classList.add('slot-faction-panel-up');
    panel.style.maxHeight = `${Math.max(140, roomAbove)}px`;
  } else {
    // Stay below but cap the height so it doesn't run off-screen.
    panel.style.maxHeight = `${Math.max(140, roomBelow)}px`;
  }
}

/** In-progress edit cache for the seed input. Persists across re-renders
 *  triggered by state patches so the user's typing doesn't get clobbered
 *  by the seed value the server keeps sending us. `null` = no pending
 *  edit, fall back to `state.seed`. */
let pendingSeedText: string | null = null;
/** Same idea for the continent-count input. */
let pendingContText: string | null = null;
/** Index of the focused options input at the start of renderSide
 *  (0 = seed, 1 = continents, -1 = nothing). Used to restore focus +
 *  caret position after the panel has been rebuilt. */
let focusedInputAtRenderStart: number = -1;

function renderSide(host: HTMLElement, state: LobbyState, _factions: FactionDef[], room: Room): void {
  // Capture which (if any) of the options inputs currently owns focus so
  // we can restore it after the host innerHTML wipe below. The two
  // option inputs share the same `.opt-seed-input` class — we identify
  // them by position (0 = seed, 1 = continents).
  const active = document.activeElement;
  focusedInputAtRenderStart = -1;
  if (active && host.contains(active) && active.matches('.opt-seed-input')) {
    const all = Array.from(host.querySelectorAll<HTMLInputElement>('.opt-seed-input'));
    focusedInputAtRenderStart = all.indexOf(active as HTMLInputElement);
  }

  host.innerHTML = '';
  const me = auth.user?.id ?? '';
  const isHost = state.hostUserId === me;
  const mySlot = findSlot(state, (s) => s.type === 'human' && s.id === me);

  // ─── 1. Ready button (the local player's "I'm ready" toggle) ────────────
  if (mySlot) {
    const ready = document.createElement('button');
    ready.type = 'button';
    ready.className = mySlot.ready ? 'btn-secondary room-side-btn' : 'btn-primary room-side-btn';
    const readyIcon = document.createElement('img');
    readyIcon.src = mySlot.ready ? '/sprites/iconchat_refus.png' : '/sprites/iconchat_valide.png';
    readyIcon.className = 'inline-icon';
    ready.appendChild(readyIcon);
    ready.appendChild(document.createTextNode(mySlot.ready ? 'Annuler prêt' : 'Je suis prêt'));
    ready.disabled = !mySlot.factionId;
    ready.title = !mySlot.factionId ? 'Choisis un royaume d\'abord' : '';
    ready.addEventListener('click', () => room.send('ready.toggle'));
    host.appendChild(ready);
  }

  // ─── 2. Options panel (seed) — host-editable, others read-only ─────────
  // IMPORTANT: state patches re-render this whole side panel, which would
  // normally wipe the user's in-progress typing. We persist the pending
  // text in a module-level cache (`pendingSeedText` / `pendingContText`),
  // restored after each render. The value is committed to the server only
  // when the user clicks "Régénérer" or blurs the continent input.
  const opts = el('div', 'room-opts');
  opts.appendChild(el('h2', 'panel-title room-options-title', 'Options'));
  const seedRow = el('div', 'opt-row');
  seedRow.appendChild(el('span', 'opt-label', 'Seed de la carte'));
  if (isHost) {
    const seedInput = document.createElement('input');
    seedInput.type = 'number';
    seedInput.value = pendingSeedText ?? state.seed.toString();
    seedInput.className = 'auth-input opt-seed-input';
    // Track every keystroke so the next state-patch re-render can restore
    // the input without losing what the user just typed.
    seedInput.addEventListener('input', () => {
      pendingSeedText = seedInput.value;
    });
    const seedBtn = document.createElement('button');
    seedBtn.type = 'button';
    seedBtn.className = 'btn-secondary opt-seed-btn';
    seedBtn.textContent = 'Régénérer';
    seedBtn.addEventListener('click', () => {
      const n = parseInt(seedInput.value, 10);
      if (Number.isFinite(n)) {
        pendingSeedText = null;           // commit → clear the pending edit
        room.send('map.regen', { seed: n });
      }
    });
    const wrap = el('div', 'opt-row-edit');
    wrap.append(seedInput, seedBtn);
    seedRow.appendChild(wrap);
  } else {
    seedRow.appendChild(el('span', 'opt-value', state.seed.toString()));
  }
  opts.appendChild(seedRow);

  // Continent count — 3..12. Affects map generation; only the host can
  // tweak it. Min 3 so the game still feels like Risk; max 12 keeps
  // continents from becoming microscopic on the default 1920x1080 map.
  const contRow = el('div', 'opt-row');
  contRow.appendChild(el('span', 'opt-label', 'Nombre de continents'));
  const currentCount = state.mapContinentCount || 9;
  if (isHost) {
    const contInput = document.createElement('input');
    contInput.type = 'number';
    contInput.min = '3';
    contInput.max = '12';
    contInput.step = '1';
    contInput.value = pendingContText ?? currentCount.toString();
    contInput.className = 'auth-input opt-seed-input';
    contInput.addEventListener('input', () => {
      pendingContText = contInput.value;
    });
    contInput.addEventListener('change', () => {
      const n = parseInt(contInput.value, 10);
      if (Number.isFinite(n)) {
        const clamped = Math.max(3, Math.min(12, n));
        contInput.value = clamped.toString();
        pendingContText = null;           // commit → clear the pending edit
        room.send('map.setContinents', { n: clamped });
      }
    });
    const wrap2 = el('div', 'opt-row-edit');
    wrap2.appendChild(contInput);
    contRow.appendChild(wrap2);
  } else {
    contRow.appendChild(el('span', 'opt-value', currentCount.toString()));
  }
  opts.appendChild(contRow);

  // Barbarian raids — host-only toggle that decides whether the random
  // "Invasion barbare" event can fire during the game. Default ON
  // (the schema initialises `barbariansEnabled = true`). Players that
  // aren't the host see a read-only "Oui/Non" label.
  const barbRow = el('div', 'opt-row');
  barbRow.appendChild(el('span', 'opt-label', 'Invasions barbares'));
  const barbEnabled = state.barbariansEnabled !== false; // default true
  if (isHost) {
    const wrap3 = el('label', 'opt-row-edit opt-row-toggle');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = barbEnabled;
    checkbox.className = 'opt-checkbox';
    checkbox.addEventListener('change', () => {
      room.send('options.setBarbarians', { enabled: checkbox.checked });
    });
    const lbl = document.createElement('span');
    lbl.className = 'opt-toggle-label';
    lbl.textContent = barbEnabled ? 'Activées' : 'Désactivées';
    // Live-update the label as the user toggles, before the server
    // state echoes back.
    checkbox.addEventListener('change', () => {
      lbl.textContent = checkbox.checked ? 'Activées' : 'Désactivées';
    });
    wrap3.append(checkbox, lbl);
    barbRow.appendChild(wrap3);
  } else {
    barbRow.appendChild(el('span', 'opt-value', barbEnabled ? 'Activées' : 'Désactivées'));
  }
  opts.appendChild(barbRow);

  host.appendChild(opts);

  // After this render, restore focus/caret to whichever option input the
  // user was editing — so a state patch that arrives mid-typing doesn't
  // visibly steal the cursor.
  if (focusedInputAtRenderStart >= 0) {
    const targetIndex = focusedInputAtRenderStart;
    focusedInputAtRenderStart = -1;
    queueMicrotask(() => {
      const all = host.querySelectorAll<HTMLInputElement>('.opt-seed-input');
      const restored = all[targetIndex];
      if (restored) {
        restored.focus();
        const len = restored.value.length;
        try { restored.setSelectionRange(len, len); } catch { /* number inputs may not support it */ }
      }
    });
  }

  // ─── 2.5 Ready-to-fight arena — shows the chosen kingdoms' army-heads ──
  // facing off, with a small idle wobble. Updates live as players pick
  // factions (each render fires from a state patch). Encourages players
  // to pick: the arena populates one combatant at a time as everyone locks
  // in their kingdom.
  host.appendChild(renderArena(state));

  // ─── 3. Start-game button (host only) ───────────────────────────────────
  if (isHost) {
    const active: PlayerSlot[] = [];
    state.slots.forEach((s) => {
      if (s.type === 'human' || s.type.startsWith('ai_')) active.push(s);
    });
    const humans: PlayerSlot[] = [];
    state.slots.forEach((s) => {
      if (s.type === 'human') humans.push(s);
    });
    const allHumansReady = humans.every((s) => s.ready && s.factionId !== '');
    const allFactionsPicked = active.every((s) => s.factionId !== '');
    const reasons: string[] = [];
    if (active.length < 2) reasons.push('≥ 2 participants');
    if (!allHumansReady) reasons.push('humains pas tous prêts');
    if (!allFactionsPicked) reasons.push('royaumes pas tous choisis');

    const startBtn = document.createElement('button');
    startBtn.type = 'button';
    startBtn.className = 'btn-primary room-side-btn room-side-start';
    const sIcon = document.createElement('img');
    sIcon.src = '/sprites/iconchat_valide.png';
    sIcon.className = 'inline-icon';
    startBtn.appendChild(sIcon);
    startBtn.appendChild(document.createTextNode('Démarrer la partie'));
    startBtn.disabled = reasons.length > 0 || state.phase !== 'lobby';
    startBtn.addEventListener('click', () => {
      // Click counts as the required user gesture for browser autoplay
      // rules — kick the music off NOW so the AudioContext is created
      // inside a fresh user-gesture handler.
      startMusic();
      room.send('game.start');
    });
    host.appendChild(startBtn);

    if (reasons.length > 0) {
      host.appendChild(el('p', 'room-side-hint', `Manquant : ${reasons.join(' · ')}`));
    }
  } else {
    host.appendChild(el('p', 'room-side-hint', 'L\'hôte démarrera la partie quand tout le monde sera prêt.'));
  }
}

function renderFooter(host: HTMLElement, _state: LobbyState, _room: Room, _router: Router): void {
  // Footer is empty now — start button moved into the side panel so the
  // whole UI fits in the viewport without a bottom strip.
  host.innerHTML = '';
}

// ----------------------------------------------------------------------------

function slotDisplayName(slot: PlayerSlot): string {
  if (slot.type === 'open') return '— libre —';
  if (slot.type === 'closed') return '— fermé —';
  return slot.name || slot.type;
}

function slotShortLabel(t: SlotType): string {
  switch (t) {
    case 'open':
      return 'Libre';
    case 'ai_easy':
      return 'IA·F';
    case 'ai_medium':
      return 'IA·M';
    case 'ai_hard':
      return 'IA·D';
    case 'closed':
      return 'Fermé';
    default:
      return t;
  }
}

function slotLongLabel(t: SlotType): string {
  switch (t) {
    case 'open': return 'Libre — un autre joueur peut rejoindre';
    case 'ai_easy': return 'IA Facile (Chaton novice)';
    case 'ai_medium': return 'IA Moyenne (Chat des rues)';
    case 'ai_hard': return 'IA Difficile (Matou expert)';
    case 'closed': return 'Fermé — personne ne peut prendre ce slot';
    default: return t;
  }
}

function findSlot(state: LobbyState, pred: (s: PlayerSlot) => boolean): PlayerSlot | undefined {
  for (let i = 0; i < state.slots.length; i++) {
    const s = state.slots[i];
    if (s && pred(s)) return s;
  }
  return undefined;
}

/**
 * "Ready-to-fight arena" — a compact horizontal panel that shows every
 * slot that has picked a faction as a small army-head sprite. Each head
 * idles with a subtle wobble (CSS animation) so the panel feels alive.
 * As more players pick, the row populates left-to-right — encourages
 * everyone to commit.
 */
function renderArena(state: LobbyState): HTMLElement {
  const arena = el('div', 'room-arena');
  const title = el('div', 'room-arena-title', '🐾 Les royaumes sont prêts à griffer');
  arena.appendChild(title);

  const stage = el('div', 'room-arena-stage');
  const fighters: PlayerSlot[] = [];
  state.slots.forEach((s) => {
    if (!s) return;
    if (s.type === 'open' || s.type === 'closed') return;
    if (!s.factionId) return;
    fighters.push(s);
  });

  if (fighters.length === 0) {
    stage.appendChild(el(
      'div',
      'room-arena-empty',
      'Personne n\'a encore sorti ses griffes — choisis un royaume !',
    ));
  } else {
    for (const s of fighters) {
      const fighter = el('div', 'room-arena-fighter');
      fighter.style.setProperty('--bob-delay', `${(s.index * 0.18).toFixed(2)}s`);
      // Randomized horizontal sway parameters → each fighter wanders at
      // its own pace, with its own pauses, sometimes drifting left first,
      // sometimes right. Combined with the head's own bob animation this
      // reads as a chaotic-but-cute crowd of cats fidgeting.
      //   • march-dur : 3.0–7.0 s (cycle length → speed)
      //   • march-delay: 0 to -dur seconds (phase offset → starting
      //     direction; some heads pause-left while others pause-right)
      // Mulberry-style deterministic-ish hash per slot index keeps the
      // values stable across renders (no jitter when the panel rebuilds).
      const rngBase = (s.index * 9301 + 49297) % 233280;
      const dur = 3.0 + ((rngBase % 4000) / 1000); // 3.0–7.0 s
      const delay = -((rngBase % 5000) / 1000); // 0 to -5 s
      fighter.style.setProperty('--march-dur', `${dur.toFixed(2)}s`);
      fighter.style.setProperty('--march-delay', `${delay.toFixed(2)}s`);
      fighter.title = `${s.name || 'Anonyme'}${s.ready ? ' · prêt' : ' · pas prêt'}`;
      const img = document.createElement('img');
      img.src = `/sprites/${s.factionId}_army.png`;
      img.className = 'room-arena-head';
      img.alt = s.name || s.factionId;
      fighter.appendChild(img);
      // Ready-state badge in the top-right of the head — iconchat_valide
      // when ready, iconchat_refus when not. Replaces the previous ring
      // colour so the indicator reads as a true cat-themed status icon.
      const badge = document.createElement('img');
      badge.src = s.ready ? '/sprites/iconchat_valide.png' : '/sprites/iconchat_refus.png';
      badge.className = s.ready
        ? 'room-arena-badge room-arena-badge-ok'
        : 'room-arena-badge room-arena-badge-no';
      badge.alt = s.ready ? 'prêt' : 'pas prêt';
      fighter.appendChild(badge);
      stage.appendChild(fighter);
    }
  }
  arena.appendChild(stage);
  return arena;
}
